(() => {
  const FREE_EBOOK_VARIANT_ID = 47772981133477;
  const FREE_EBOOK_MAX_ATTEMPTS = 4;
  if (window.clcReCartHooks) return;
  window.clcReCartHooks = true;

  const onClcRePage = () =>
    document.body.classList.contains('body-clc-revitaleyes') ||
    !!document.querySelector('[data-clc-re]');

  const cartJsonHeaders = () => ({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input && input.url ? String(input.url) : '';
  };

  // The free UnDo eBook is added by assets/product-form.js, which only runs inside
  // <product-form-component>. These sections ship their own form, so that never fires
  // here — add the gift once the page's own cart adds have settled.
  const isFreeEbookItem = (item) =>
    Number((item && (item.id || item.variant_id)) || 0) === FREE_EBOOK_VARIANT_ID;

  // Which product this page actually sells. Both sections render data-product-id;
  // Shopify's own page meta is the backstop. If neither names a product we cannot
  // establish eligibility, so the gift is skipped rather than guessed at.
  const clcReProductIds = () => {
    const ids = [...document.querySelectorAll('[data-clc-re][data-product-id]')]
      .map((el) => Number(el.dataset.productId))
      .filter(Boolean);
    if (ids.length) return ids;
    const metaId = Number(
      (window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.id) || 0
    );
    return metaId ? [metaId] : [];
  };

  // The theme's gift logic runs inside the <product-form-component> that sells a given
  // product, and each one names its own product. Only a form for THIS page's product
  // means the gift is already handled; a cross-sell or quick-add form for some other
  // product says nothing about how the page product is sold.
  const themeFormSellsPageProduct = (productIds) =>
    [...document.querySelectorAll('product-form-component[data-product-id]')].some((el) =>
      productIds.includes(Number(el.dataset.productId))
    );

  const readCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((res) => res.json());

  const cartSignature = (items) =>
    (items || [])
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join('|');

  // The theme's own way of repainting the slide-out bag from the real cart. Given the real
  // items, it skips the repaint when the bag already shows them: every Kaching refetch fades
  // the lines and can cancel a refetch Kaching already has running.
  const refreshCartUi = async (realItems) => {
    const api = window.kachingCartApi;
    if (!api || typeof api.refreshCart !== 'function') return;
    if (realItems && typeof api.getCartData === 'function') {
      try {
        if (cartSignature(api.getCartData()?.items) === cartSignature(realItems)) return;
      } catch (error) {}
    }
    try {
      await api.refreshCart();
    } catch (error) {}
  };

  const postCart = (path, payload) =>
    fetch(path, {
      method: 'POST',
      headers: cartJsonHeaders(),
      body: JSON.stringify(payload),
    });

  const addToCart = async (source, items) => {
    if (window.BotaniqueCartAdd && typeof window.BotaniqueCartAdd.addLines === 'function') {
      const onlyEbook =
        items.length > 0 && items.every((entry) => isFreeEbookItem(entry));
      await window.BotaniqueCartAdd.addLines(items, {
        attachFreePdf: !onlyEbook,
        attachWarranty: false,
        openDrawer: false,
      });
      return;
    }
    if (window.BotaniqueAutoCart && typeof window.BotaniqueAutoCart.enqueue === 'function') {
      await window.BotaniqueAutoCart.enqueue({ source, addItems: items });
      return;
    }
    await postCart('/cart/add.js', { items });
    await refreshCartUi();
  };

  // Two things can add the same line — the theme's own form and this script — so a tie
  // would leave two copies. Put any surplus back rather than letting it reach checkout.
  const trimSurplus = async (items, matches, maxQuantity) => {
    const line = items.find(matches);
    if (!line || !line.key || Number(line.quantity) <= maxQuantity) return false;
    await postCart('/cart/change.js', { id: line.key, quantity: maxQuantity });
    return true;
  };

  // The page's own form always names the variant it is about to add, so an add that
  // never reaches the cart can be put right instead of silently going missing.
  const readPendingAdd = (form) => {
    const variantField = form.querySelector('[name="id"]');
    const id = Number(variantField && variantField.value);
    if (!id) return null;
    const quantityField = form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, Number((quantityField && quantityField.value) || 1));
    return { id, quantity, at: Date.now() };
  };

  // One click sets off several independent cart writes — the bundle's free gift, the
  // page's own product, the warranty toggle and this gift — and the bag paints from
  // whichever response lands first, so it could settle on a half-filled cart. After the
  // adds go quiet, re-read the real cart, put right whatever is genuinely missing, and
  // repaint the bag from what is actually in there.
  const RECONCILE_DELAYS = [400, 1100, 2000, 3200, 5000, 7500, 10500, 14000];
  // One cycle per settled cart add; a generous lifetime guard against a runaway loop.
  const RECONCILE_MAX_CYCLES = 60;
  // Only step in for the page's own product once its add has had every chance to land:
  // nothing in flight, the adds quiet for a while, and well past the submit itself.
  const PRODUCT_RECOVERY_QUIET_MS = 4000;
  const PRODUCT_RECOVERY_DELAY_MS = 12000;
  // Where a theme form sells this product it gifts the eBook itself — let it go first.
  const THEME_GIFT_GRACE_TICKS = 2;

  let ebookAttempts = 0;
  let reconcileBusy = false;
  let reconcileSettled = false;
  let cycleTicks = 0;
  let cycles = 0;
  let reconcileTimers = [];
  let cartAddId = 0;
  let refreshedForAddId = -1;
  let lastAddSettledAt = 0;
  let cartAddsInFlight = 0;
  let pendingAdd = null;
  let productRecovered = false;

  const reconcile = async () => {
    if (reconcileSettled || reconcileBusy || !onClcRePage()) return;
    reconcileBusy = true;
    const addId = cartAddId;
    const tick = cycleTicks;
    try {
      const cart = await readCart();
      const items = cart.items || [];
      const productIds = clcReProductIds();
      // No identified page product => eligibility is unknown => leave the cart alone.
      if (!productIds.length) return;

      // Never let a double gift survive; re-read on the next tick.
      if (await trimSurplus(items, isFreeEbookItem, 1)) return;
      if (
        productRecovered &&
        pendingAdd &&
        (await trimSurplus(
          items,
          (item) => Number(item.variant_id || item.id) === pendingAdd.id,
          pendingAdd.quantity
        ))
      ) {
        return;
      }

      if (!items.some((item) => productIds.includes(Number(item.product_id)))) {
        const quiet =
          cartAddsInFlight === 0 && lastAddSettledAt && Date.now() - lastAddSettledAt >= PRODUCT_RECOVERY_QUIET_MS;
        // The device genuinely never made it into the bag: put the page's own add
        // through once, rather than leaving the shopper with the free gifts only.
        if (!productRecovered && pendingAdd && quiet && Date.now() - pendingAdd.at >= PRODUCT_RECOVERY_DELAY_MS) {
          productRecovered = true;
          await addToCart('clc-re-product-recovery', [
            { id: pendingAdd.id, quantity: pendingAdd.quantity },
          ]);
        }
        // Still nothing of this page's own in the bag; wait for the next tick.
        return;
      }

      const ebookInCart = items.some(isFreeEbookItem);
      // Whether the gift is handled is read off the cart itself, never assumed from the
      // markup — the theme form only buys itself a couple of ticks' head start.
      const waitingOnThemeForm =
        !ebookInCart && tick <= THEME_GIFT_GRACE_TICKS && themeFormSellsPageProduct(productIds);

      if (!ebookInCart && !waitingOnThemeForm && ebookAttempts < FREE_EBOOK_MAX_ATTEMPTS) {
        ebookAttempts += 1;
        await addToCart('clc-re-free-ebook-add', [{ id: FREE_EBOOK_VARIANT_ID, quantity: 1 }]);
        return;
      }

      if (refreshedForAddId !== addId) {
        refreshedForAddId = addId;
        await refreshCartUi(items);
      }
      // Only stop once the bag really does hold everything it should.
      if (ebookInCart) reconcileSettled = true;
    } catch (error) {
      console.error('CLC RevitalEyes cart reconcile failed:', error);
    } finally {
      reconcileBusy = false;
    }
  };

  const scheduleReconcile = () => {
    if (!onClcRePage() || cycles >= RECONCILE_MAX_CYCLES) return;
    cycles += 1;
    // The budget is per add cycle, so a shopper who keeps adding keeps being looked
    // after — only the page-lifetime cycle count is bounded.
    cycleTicks = 0;
    reconcileSettled = false;
    reconcileTimers.forEach(clearTimeout);
    reconcileTimers = RECONCILE_DELAYS.map((delay) =>
      setTimeout(() => {
        cycleTicks += 1;
        reconcile();
      }, delay)
    );
  };

  let idleWaiters = [];
  const noteCartAdd = (request) => {
    cartAddsInFlight += 1;
    Promise.resolve(request).finally(() => {
      cartAddsInFlight = Math.max(0, cartAddsInFlight - 1);
      cartAddId += 1;
      if (cartAddsInFlight === 0) {
        lastAddSettledAt = Date.now();
        window.__clcReCartAddSettledAt = performance.now();
        const waiters = idleWaiters;
        idleWaiters = [];
        waiters.forEach((resolve) => resolve());
        scheduleReconcile();
      }
    });
  };

  // Kaching posts the bundle's free cream a moment before the page's own add. Two cart
  // writes racing each other can leave only one of them in the bag (the cream without the
  // device), so the page's add waits for Kaching's to finish, up to maxMs.
  window.__clcReWaitForCartIdle = (maxMs) =>
    cartAddsInFlight === 0
      ? Promise.resolve()
      : new Promise((resolve) => {
          idleWaiters.push(resolve);
          setTimeout(resolve, maxMs || 2500);
        });

  // Called by the form handler once it has seen the device in the bag, so the 12 s
  // recovery above does not put it in a second time.
  window.__clcReMarkProductHandled = () => {
    pendingAdd = null;
  };

  // Start watching the moment the shopper submits, not only once an add has settled —
  // the page's own add can land several seconds after the bundle's free gift — and keep
  // what it was adding, so a dropped add can be put right.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || typeof form.closest !== 'function') return;
    const reForm = form.closest('[data-clc-re] .re-form');
    if (!reForm) return;
    pendingAdd = readPendingAdd(reForm);
    productRecovered = false;
    cycles = 0;
    scheduleReconcile();
  }, true);
  const isCartAddUrl = (url) => /\/cart\/add(\.js)?(\?|#|$)/.test(url);

  // Cart requests are passed straight through — the wrapper only watches when the
  // page's own adds finish, which is the signal the eBook gift waits on.
  const wrapFetch = (fetchFn) => {
    const wrapped = function (input, init) {
      const response = fetchFn(input, init);
      if (
        onClcRePage() &&
        isCartAddUrl(requestUrl(input)) &&
        response &&
        typeof response.then === 'function'
      ) {
        noteCartAdd(response);
      }
      return response;
    };
    wrapped.__clcReWrapped = true;
    return wrapped;
  };

  if (!XMLHttpRequest.prototype.send.__clcReWrapped) {
    const xhrOpen = XMLHttpRequest.prototype.open;
    const xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__clcReUrl = String(url || '');
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (onClcRePage() && isCartAddUrl(this.__clcReUrl || '')) {
        noteCartAdd(new Promise((resolve) => {
          this.addEventListener('loadend', resolve, { once: true });
        }));
      }
      return xhrSend.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send.__clcReWrapped = true;
  }

  if (!window.fetch.__clcReWrapped) window.fetch = wrapFetch(window.fetch.bind(window));

  const wrapKachingOriginal = () => {
    if (typeof window.__kachingCartOriginalFetch === 'function' && !window.__kachingCartOriginalFetch.__clcReWrapped) {
      window.__kachingCartOriginalFetch = wrapFetch(window.__kachingCartOriginalFetch.bind(window));
    }
  };
  wrapKachingOriginal();
  document.addEventListener('DOMContentLoaded', wrapKachingOriginal);
  setTimeout(wrapKachingOriginal, 1500);

})();

(() => {
  const WOMEN_VARIANTS = { white: '45694252777637', blush: '45694253990053', black: '45694254022821', pink: '45694253957285' };
  const MEN_VARIANTS = { white: '46037260697765', black: '46037260763301' };
  const init = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const isMen = root.dataset.clcRe === 'men';
    const VARIANTS = root.dataset.variants ? JSON.parse(root.dataset.variants) : isMen ? MEN_VARIANTS : WOMEN_VARIANTS;
    const productId = root.dataset.productId || (isMen ? '9259506335909' : '8314937278629');
    const q = (s) => root.querySelector(s);
    const qa = (s) => [...root.querySelectorAll(s)];
    qa('.vposter').forEach((img) => {
      const src = img.currentSrc || img.src;
      if (!src) return;
      const warm = new Image();
      warm.decoding = 'async';
      warm.src = src;
    });
    const stage = q('.stage img');
    const rail = q('.rail');
    const thumbs = qa('.thumb');
    const productGallery = root.hasAttribute('data-product-gallery');
    const imagePath = (url) => {
      try { return new URL(url, location.href).pathname; }
      catch { return ''; }
    };
    const freezeWindow = (fn) => {
      const x = window.scrollX;
      const y = window.scrollY;
      const left = q('.left');
      const leftTop = left ? left.scrollTop : 0;
      const restore = () => {
        window.scrollTo(x, y);
        if (left) left.scrollTop = leftTop;
      };
      fn();
      restore();
      requestAnimationFrame(restore);
    };

    rail?.addEventListener('click', (e) => {
      const b = e.target.closest('.thumb');
      if (!b) return;
      freezeWindow(() => {
        stage.src = productGallery ? b.dataset.full : b.querySelector('img').src;
        if (productGallery) stage.alt = b.querySelector('img').alt;
        thumbs.forEach((x) => x.setAttribute('aria-current', String(x === b)));
        const a = rail.getBoundingClientRect();
        const r = b.getBoundingClientRect();
        if (rail.scrollHeight > rail.clientHeight + 2) {
          rail.scrollTop += r.top < a.top ? r.top - a.top : r.bottom > a.bottom ? r.bottom - a.bottom : 0;
        } else {
          rail.scrollLeft += r.left < a.left ? r.left - a.left : r.right > a.right ? r.right - a.right : 0;
        }
      });
    });

    q('.cp-row')?.addEventListener('click', (e) => {
      const b = e.target.closest('.sw');
      if (!b) return;
      freezeWindow(() => {
        qa('.sw').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        q('.cp-name').textContent = b.dataset.name;
        q('[data-re-variant]').value = VARIANTS[b.dataset.c] || VARIANTS[b.dataset.name.toLowerCase().replace(/\s+/g, '-')];
        const url = b.dataset.hero;
        if (url) {
          stage.src = url;
          if (productGallery) {
            // Select the existing product image without replacing the merchant's first thumbnail.
            const selected = thumbs.find((thumb) => imagePath(thumb.querySelector('img').src) === imagePath(url));
            thumbs.forEach((thumb) => thumb.setAttribute('aria-current', String(thumb === selected)));
            if (selected) stage.alt = selected.querySelector('img').alt;
          } else {
            thumbs[0].querySelector('img').src = url;
            thumbs.forEach((x, i) => x.setAttribute('aria-current', String(i === 0)));
          }
        }
      });
    });

    const tabs = qa('[role="tab"]');
    const panels = qa('[role="tabpanel"]');
    const videos = qa('.vcell');
    const attachSrc = (v) => {
      if (!v || !v.dataset.src || v.getAttribute('src')) return false;
      v.playsInline = true;
      v.setAttribute('webkit-playsinline', '');
      v.preload = 'metadata';
      v.src = v.dataset.src;
      return true;
    };
    const showPoster = (v) => {
      if (!v) return;
      if (v.getAttribute('poster') || v.closest('.vcell')?.querySelector('.vposter')) return;
      const ready = () => v.classList.add('is-ready');
      if (v.readyState >= 2) {
        ready();
        return;
      }
      v.addEventListener('loadeddata', () => {
        if (!v.paused) {
          ready();
          return;
        }
        try {
          if (v.currentTime < 0.05) v.currentTime = 0.1;
        } catch (e) {}
        ready();
      }, { once: true });
      attachSrc(v);
    };
    const pickTab = (i) => {
      tabs.forEach((x, n) => {
        x.setAttribute('aria-selected', String(n === i));
        x.tabIndex = n === i ? 0 : -1;
      });
      panels.forEach((x, n) => {
        x.dataset.on = String(n === i);
        x.hidden = n !== i;
      });
      if (i === 1) videos.forEach((cell) => showPoster(cell.querySelector('video')));
      else videos.forEach(stopCell);
    };
    const stopCell = (cell) => {
      const vid = cell.querySelector('video');
      vid.pause();
      vid.controls = false;
      cell.dataset.playing = 'false';
    };
    const playCell = (cell) => {
      const v = cell.querySelector('video');
      videos.forEach((o) => { if (o !== cell) stopCell(o); });
      attachSrc(v);
      v.controls = true;
      v.classList.add('is-ready');
      cell.dataset.playing = 'true';
      const attempt = v.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          cell.dataset.playing = 'false';
          v.controls = false;
        });
      }
    };
    tabs.forEach((x, i) => x.addEventListener('click', () => pickTab(i)));
    pickTab(0);
    videos.forEach((cell) => {
      const v = cell.querySelector('video');
      cell.querySelector('.vplay')?.addEventListener('click', () => playCell(cell));
      cell.querySelector('.vfull')?.addEventListener('click', () => {
        attachSrc(v);
        const f = v.webkitEnterFullscreen || v.webkitRequestFullscreen || v.requestFullscreen;
        if (f) f.call(v);
        playCell(cell);
      });
      v.addEventListener('ended', () => stopCell(cell));
      v.addEventListener('error', () => stopCell(cell));
    });

    const quotes = qa('.rw-q');
    const dots = qa('.dot');
    const namesAttr = root.getAttribute('data-review-names');
    const names = namesAttr
      ? namesAttr.split('|').map((n) => n.trim()).filter(Boolean)
      : ['Patricia K.', 'Maria S.', 'Jessica L.', 'Anje F.', 'Rachel D.'];
    const avatarsAttr = root.getAttribute('data-review-avatars');
    const avatars = avatarsAttr
      ? avatarsAttr.split('|').map((n) => n.trim()).filter(Boolean)
      : [];
    let ri = 0;
    let timer;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const show = (i) => {
      ri = i;
      quotes.forEach((x, n) => x.setAttribute('aria-hidden', String(n !== i)));
      dots.forEach((x, n) => x.setAttribute('aria-current', String(n === i)));
      if (names[i]) q('.rw-name').textContent = names[i];
      if (avatars[i] && q('.rw-av')) q('.rw-av').src = avatars[i];
    };
    const loop = () => {
      clearInterval(timer);
      if (!reduced) timer = setInterval(() => show((ri + 1) % quotes.length), 7000);
    };
    dots.forEach((x, i) => x.addEventListener('click', () => {
      show(i);
      loop();
    }));
    loop();

    let requestedDeal = 0;
    const chooseDeal = (button) => {
      if (!button) return;
      qa('.opt').forEach((x) => x.setAttribute('aria-pressed', String(x === button)));
      const request = ++requestedDeal;
      let id = button.dataset.kachingDeal;
      const submit = q('.cta');
      submit.disabled = true;
      let attempts = 0;
      const sync = () => {
        if (request !== requestedDeal) return;
        // Deal IDs in the markup belong to the original product; fall back to the bar at the same position
        const bars = [...document.querySelectorAll('kaching-bundle[product-id="' + productId + '"] .kaching-bundles__bar')];
        const bar = bars.find((x) => x.dataset.dealBarId === id) || bars[qa('.opt').indexOf(button)];
        if (bar) id = bar.dataset.dealBarId || id;
        if (bar) {
          try {
            (bar.querySelector('label') || bar.querySelector('[role="button"]'))?.click();
          } catch (error) {}
        }
        const field = q('[name="properties[__kaching_bundles]"]');
        let fieldReady = false;
        try {
          fieldReady = JSON.parse(field?.value || '{}').bar === id;
        } catch (error) {}
        const selected = bar?.classList.contains('kaching-bundles__bar--selected');
        if (fieldReady || (!field && selected)) {
          q('[data-cart-status]').textContent = '';
          submit.disabled = submit.dataset.available === 'false';
          return;
        }
        if (++attempts < 40) setTimeout(sync, 100);
        else q('[data-cart-status]').textContent = 'Purchase options are still loading. Please select an option again.';
      };
      sync();
    };
    q('.cta').type = 'submit';
    qa('.opt').forEach((x) => {
      x.disabled = false;
      x.addEventListener('click', () => chooseDeal(x));
    });
    chooseDeal(q('.opt[aria-pressed="true"]'));

    const stockNode = q('[data-low-stock-count]');
    if (stockNode) {
      const key = 'clc-re-stock-' + location.pathname;
      let stock;
      try {
        stock = JSON.parse(localStorage.getItem(key) || 'null');
      } catch (error) {}
      const now = Date.now();
      if (!stock || now - stock.start >= 864e5) {
        stock = { value: 9, start: now, last: now, next: (20 + Math.floor(Math.random() * 21)) * 6e4 };
      }
      while (stock.value > 3 && now - stock.last >= stock.next) {
        stock.value--;
        stock.last += stock.next;
        stock.next = (20 + Math.floor(Math.random() * 21)) * 6e4;
      }
      try {
        localStorage.setItem(key, JSON.stringify(stock));
      } catch (error) {}
      stockNode.textContent = stock.value;
    }

    // Left alone, Kaching posts this form itself (sometimes followed by a full page load to
    // /cart) and the eBook needs a second request. Kaching Cart steps aside when the page
    // calls preventDefault, even after its own capture listener already has, so the
    // defaultPrevented flag is not a signal here. Kaching's extra items are added before
    // this submit fires.
    let formAddInFlight = false;
    q('.re-form')?.addEventListener('submit', async (event) => {
      const cartAdd = window.BotaniqueCartAdd;
      if (!cartAdd || typeof cartAdd.addFromForm !== 'function') return;
      const form = event.currentTarget;
      if (!cartAdd.lineItemFromFormData(new FormData(form))) return;
      event.preventDefault();
      if (formAddInFlight) return;
      formAddInFlight = true;
      const formData = new FormData(form);
      const pageVariantId = Number(formData.get('id'));
      const requestedQty = Math.max(1, Number(formData.get('quantity')) || 1);
      const requestedPlan = Number(formData.get('selling_plan')) || null;
      // Units of exactly this request (same variant, same selling plan) in the bag. The bag
      // may already hold the device, so success is measured as a gain over the count taken
      // before this submit, never as mere presence.
      const deviceQtyInCart = async () => {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`Cart read failed (${res.status})`);
        const cart = await res.json();
        if (!Array.isArray(cart && cart.items)) throw new Error('Cart read returned no items');
        return cart.items
          .filter((item) => Number(item.variant_id) === pageVariantId)
          .filter((item) => (Number(item.selling_plan_allocation?.selling_plan?.id) || null) === requestedPlan)
          .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      };
      const waitIdle = async () => {
        if (typeof window.__clcReWaitForCartIdle === 'function') {
          await window.__clcReWaitForCartIdle(2500);
        }
      };
      const addDevice = () =>
        cartAdd.addFromForm(form, { attachFreePdf: true, attachKachingWarranty: true });
      try {
        await waitIdle();
        // The starting count is best-effort: a cart read that fails (a rate limit, a bad
        // answer) must never stop the add itself. Without it there is nothing to measure a
        // gain against, so the add's own answer decides: an add that answered OK is trusted
        // as it is, and only an add that was refused is sent once more.
        let qtyBefore = null;
        if (pageVariantId) {
          try {
            qtyBefore = await deviceQtyInCart();
          } catch (error) {
            qtyBefore = null;
          }
        }
        // A line add is all-or-nothing, so no gain means it was lost and is safe to repeat;
        // a partial gain (stock cap) must not be topped up with a second full add. A read
        // that fails here counts as "unknown" and leads to no extra add.
        const gained = async () => {
          if (qtyBefore === null) return null;
          try {
            return (await deviceQtyInCart()) - qtyBefore;
          } catch (error) {
            return null;
          }
        };
        try {
          await addDevice();
        } catch (error) {
          // A refused add (rate limit, a write that collided with Kaching's) would leave
          // the free cream alone in the bag. Give it one more go once things are quiet,
          // unless this request's units made it in after all.
          await waitIdle();
          await new Promise((resolve) => setTimeout(resolve, 600));
          // With no count to compare, a failed add is simply sent once more.
          const gain = await gained();
          if (gain === null || gain <= 0) await addDevice();
        }
        // The add answered OK, but a write that raced it can still have dropped the device.
        if (qtyBefore !== null) {
          await waitIdle();
          const gain = await gained();
          if (gain !== null && gain <= 0) await addDevice();
          const finalGain = await gained();
          if (finalGain !== null && finalGain >= requestedQty && typeof window.__clcReMarkProductHandled === 'function') {
            window.__clcReMarkProductHandled();
          }
        }
      } catch (error) {
        console.error('CLC RevitalEyes add to cart failed:', error);
        const status = q('[data-cart-status]');
        if (status) status.textContent = error.message || 'Unable to add to cart. Please try again.';
      } finally {
        formAddInFlight = false;
      }
    });

    let addingWatch = 0;
    q('.re-form')?.addEventListener('submit', () => {
      const status = q('[data-cart-status]');
      if (!status) return;
      status.textContent = 'Adding to cart…';
      const watchId = ++addingWatch;
      const startedAt = performance.now();
      let sawLoading = document.body.classList.contains('bp-cart-add-loading');
      const tick = () => {
        if (watchId !== addingWatch) return;
        const loading = document.body.classList.contains('bp-cart-add-loading');
        if (loading) sawLoading = true;
        const settled = (window.__clcReCartAddSettledAt || 0) >= startedAt;
        const elapsed = performance.now() - startedAt;
        const drawerReady = !sawLoading || !loading;
        if ((settled && drawerReady) || elapsed > 15000) {
          if (status.textContent === 'Adding to cart…') status.textContent = '';
          return;
        }
        setTimeout(tick, 80);
      };
      setTimeout(tick, 80);
    });
  };

  const mount = () => document.querySelectorAll('[data-clc-re]').forEach(init);
  const hideFloatingFrontrow = () => {
    document.querySelectorAll([
      '#frontrow-sticky-regular',
      '#frontrow-sticky',
      '#frontrow-sticker',
      '.iframe-frontrow-sticker',
      'iframe[id^="frontrow-sticky"]',
      'iframe[src*="presentation_type=sticker"]',
      'iframe[src*="presentation_type=sticky"]',
    ].join(',')).forEach((el) => el.remove());
  };
  hideFloatingFrontrow();
  const frontrowWatch = new MutationObserver(hideFloatingFrontrow);
  frontrowWatch.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => frontrowWatch.disconnect(), 8000);

  mount();
  document.addEventListener('shopify:section:load', mount);

  if (!window.clcReAtcLinks) {
    window.clcReAtcLinks = true;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href="#atcSubmit"]');
      if (!link) return;
      const form = document.querySelector('[data-clc-re] .re-form');
      const submit = form?.querySelector('[type="submit"]');
      if (!form || !submit) return;
      event.preventDefault();
      submit.click();
    });
  }

  if (!window.clcReExclusiveFaq) {
    window.clcReExclusiveFaq = true;
    document.addEventListener('toggle', (event) => {
      const opened = event.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
      const faq = opened.closest('.bottom-faqs');
      if (!faq) return;
      faq.querySelectorAll('details[open]').forEach((sibling) => {
        if (sibling !== opened) sibling.open = false;
      });
    }, true);
  }
})();
